import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import React, { useContext } from 'react';
import { Carousel } from 'react-responsive-carousel';
// Imported here rather than relied on from a sibling: the deleted legacy
// page used to pull it in, and the block must not depend on some other
// component happening to be mounted.
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import ReactImageMagnify from 'react-image-magnify';
import { ProductMediaContext, ProductPageContext } from './ProductPageContext';
import { VideoComponent } from './PageComponentElementCarousel';
import OptionsSlider from './PageComponentOptionsSlider';
import OptionsItemContainer from './PageComponentOptionsItemContainer';
import { determineMediaType } from './ProductDetailShared';

// Product media gallery — the pinned first block of every product page.
//
// Mobile and desktop are BOTH rendered and picked by CSS, exactly as the
// old hardcoded page did: a swipeable carousel with dot indicators below
// sm, a two-up zoomable grid above it. The block is pinned to index 0 and
// cannot be deleted, retyped or hidden (see productPageLayout.ts), so a
// product page always leads with its media.
//
// Index and overlay state live in ProductMediaContext rather than here, so
// the fullscreen lightbox the page renders stays in sync with whichever
// slide the shopper is on.

export interface BlockProductGalleryData {
  // Desktop presentation. 'twoUpGrid' = the zoomable 2-column slider,
  // 'carousel' = reuse the mobile swipe carousel at full width.
  desktopLayout?: 'twoUpGrid' | 'carousel';
  showIndicators?: boolean;
  enableZoom?: boolean;
  // Shows the "Stok Habis" plate over a sold-out product's media.
  showStockBadge?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  // Mobile carousel: hidden once the desktop grid takes over.
  mobileGallery: {
    width: '100%',
    display: 'block',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
    marginBottom: '16px',
    position: 'relative',
  },
  galleryImage: {
    width: '100%',
    marginBottom: '36px',
  },
  indicatorItem: {
    display: 'inline-block',
    background: '#999',
    width: 8,
    height: 8,
    margin: '0px 4px',
    borderRadius: '50%',
  },
  indicatorItemSelected: {
    display: 'inline-block',
    background: '#000',
    width: 8,
    height: 8,
    margin: '0px 4px',
    borderRadius: '50%',
  },
  desktopGallery: {
    display: 'block',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  zoomTarget: {
    cursor: 'zoom-in',
  },
  soldOutPlate: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.55)',
    zIndex: 4,
    pointerEvents: 'none',
  },
  soldOutText: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '13px',
    letterSpacing: '1px',
    color: '#121212',
    backgroundColor: '#ffffff',
    border: '1px solid #121212',
    borderRadius: '20px',
    padding: '7px 18px 5px',
  },
  emptyMedia: {
    width: '100%',
    aspectRatio: '1',
    backgroundColor: '#f2f2f2',
  },
}),
);

export default function BlockProductGallery({ content }: { content: BlockProductGalleryData }) {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const page = useContext(ProductPageContext);
  const media = useContext(ProductMediaContext);

  // Rendered outside a product page (e.g. dropped into a Halaman layout).
  if (!page || !media) return null;

  const { showIndicators = true, enableZoom = true, showStockBadge = true } = content ?? {};
  const desktopLayout = content?.desktopLayout ?? 'twoUpGrid';
  const urls = media.mediaUrls;
  const soldOut = showStockBadge && !!page.product.isSoldOut;

  if (!urls.length) {
    return <Box className={classes.emptyMedia} />;
  }

  const openOverlay = (index: number) => {
    // A preview iframe must not open a fullscreen lightbox over the editor.
    if (page.isPreview) return;
    if (media.index !== index) media.setIndex(index);
    media.setOverlayOpen(true);
  };

  const renderItem = (url: string, i: number, withZoom: boolean) => {
    const contentType = determineMediaType(url);
    if (contentType === 'video') {
      return (
        <VideoComponent
          source={url}
          videoHasSound={url.includes('_with_sound')}
          videoSoundOn={url.includes('_with_sound') && !media.overlayOpen && media.index === i}
        />
      );
    }
    if (withZoom && enableZoom) {
      return (
        <ReactImageMagnify {...{
          smallImage: { alt: page.product.name, isFluidWidth: true, src: url },
          largeImage: { src: url, width: 720, height: 1080 },
        }}
          enlargedImagePosition="over"
          isHintEnabled={i === 1} />
      );
    }
    return <img src={url} alt={page.product.name} className={classes.galleryImage} />;
  };

  const mobileCarousel = (
    <Box className={classes.mobileGallery}>
      <Carousel
        selectedItem={media.index}
        preventMovementUntilSwipeScrollTolerance
        swipeScrollTolerance={12}
        onSwipeStart={media.freezeAutoslide}
        infiniteLoop
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        showIndicators={showIndicators}
        renderIndicator={(onClickHandler, isSelected, index, label) => (
          <li
            className={isSelected ? classes.indicatorItemSelected : classes.indicatorItem}
            onClick={isSelected ? undefined : onClickHandler}
            onKeyDown={isSelected ? undefined : onClickHandler}
            value={index}
            key={index}
            role="button"
            tabIndex={0}
            title={`${label} ${index + 1}`}
            aria-label={isSelected ? `Selected: ${label} ${index + 1}` : `${label} ${index + 1}`}
          />
        )}
        onChange={(index) => { if (media.index !== index) media.setIndex(index); }}
      >
        {urls.map((url, i) => (
          <div key={'product-media-' + page.productId + '-' + i} onClick={() => openOverlay(i)}>
            {renderItem(url, i, false)}
          </div>
        ))}
      </Carousel>
      {soldOut &&
        <Box className={classes.soldOutPlate}>
          <Typography className={classes.soldOutText}>STOK HABIS</Typography>
        </Box>}
    </Box>
  );

  // ReactImageMagnify is heavy and pointless on touch, so the desktop grid
  // is JS-gated rather than merely CSS-hidden.
  const desktopGrid = isDesktop ? (
    <Box className={classes.desktopGallery} style={{ position: 'relative' }}>
      {desktopLayout === 'carousel'
        ? <Carousel selectedItem={media.index} infiniteLoop showThumbs={false}
            showStatus={false} showIndicators={showIndicators}
            onChange={(index) => { if (media.index !== index) media.setIndex(index); }}>
            {urls.map((url, i) => (
              <div key={'product-media-lg-' + page.productId + '-' + i} onClick={() => openOverlay(i)}>
                {renderItem(url, i, false)}
              </div>
            ))}
          </Carousel>
        : <OptionsSlider
            title={[]}
            buttonMarginRight="24px"
            contentsChange={page.productId}
            marginDesktop="0px 0px 0px 0px"
            marginMobile="0px 0px 0px 0px"
            contents={urls.map((url, i) => (
              <OptionsItemContainer
                xsSize={6} smSize={6} mdSize={6} lgSize={6}
                content={
                  <div className={classes.zoomTarget} onClick={() => openOverlay(i)}>
                    {renderItem(url, i, true)}
                  </div>
                } />
            ))} />}
      {soldOut &&
        <Box className={classes.soldOutPlate}>
          <Typography className={classes.soldOutText}>STOK HABIS</Typography>
        </Box>}
    </Box>
  ) : null;

  return <>{mobileCarousel}{desktopGrid}</>;
}
